import React, { useState, useEffect, useMemo, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import MaterialTable from 'material-table'
import PatchedPagination from './PatchedPagination';
import LinearProgress from '@material-ui/core/LinearProgress';
import { serverContext, appContext, platformsContext, translatePlatforms } from '../App';
import Fab from '@material-ui/core/Fab';
import RecordSensorIcon from '@material-ui/icons/FiberManualRecord';
import StopIcon from '@material-ui/icons/Stop';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActionArea from '@material-ui/core/CardActionArea';
import Popover from '@material-ui/core/Popover';
import Hidden from '@material-ui/core/Hidden';
import grey from '@material-ui/core/colors/grey';
import { Tooltip } from '@material-ui/core';
import { RecorderState, AvatarByState } from './showState';
import moment from 'moment';
import MissionOrPlatformCounter from './MissionOrPlatformCounter';
import { SensorPlayer } from 'stserver-frontend-comp';
import { tableIcons } from '../commonTypes';
import { useTableTheming } from './useTableTheming';
import { getSafeLiveMode } from './liveDvr';

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiPaper-root': {
      boxShadow: 'none'
    },
  },
  '& .MuiTableCell-root': {
    paddingTop: '4px',
    paddingBottom: '4px',
    fontSize: '0.8rem',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  small: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  large: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  popover: {
    pointerEvents: 'none',
  },
  paper: {
    padding: 0,
  },
  mediaRoot: {
    width: 180,
  },
  media: {
    height: 128,
  },
  video: {
    width: 180,
    height: 128,
    elevation: 0,
    backgroundColor: grey[100],
    textAlign: 'left',
    justify: 'center'
  },
}));


function getLiveMode(useLiveLowLatency, useWebRtcVideo, useLiveRtspHls) {
  let liveMode;

  if (useLiveLowLatency)
    liveMode = 'lowLatency';
  else {
    if (useWebRtcVideo)
      liveMode = 'webRtc';
    else if (useLiveRtspHls)
      liveMode = 'highQuality';
    else
      liveMode = 'highQualityDvr';
  }
  return liveMode;
}

const LiveSensorPopover = props => {
  const classes = useStyles();
  const { server } = useContext(serverContext);
  const { data } = props;


  const liveMode = useMemo(() => {
    const defaultMode = getLiveMode(server.useLiveLowLatency, server.useWebRtcVideo, server.useLiveRtspHls);
    return getSafeLiveMode(defaultMode, data);
  }, [data, server.useLiveLowLatency, server.useWebRtcVideo, server.useLiveRtspHls]);

  return (
    <Card className={classes.mediaRoot} id='JsPlayer'>
      <CardActionArea>
        <CardContent className={classes.video} >
          <SensorPlayer
            mode={liveMode}
            webRtcService={server.webRtcService}
            autoplay={true}
            showControls={false}
            wsVideoStreamPort={data.config.wsVideoStreamPort}
            platformName={data.parentPlatformName}
            platformType={data.parentPlatformType}
            sensorName={data.sensor}
            serverUrl={server.serverHost}
            useReverseProxy={server.useReverseProxy}
            token={server.token}
            clientId={server.clientId}
            username={server.username}
            password={server.password}
            aspectRatioChangeBehaviour={'keepWidth'}
          />
        </CardContent>
      </CardActionArea>
    </Card>
  );
}


const RecordMissionIcon = () => {
  return (
    <svg height="24" width="24">
      <circle cx="12" cy="12" r="11" stroke="lightgrey" strokeWidth="1" fill="red" />
    </svg>
  )
}

const PlatformRecorderControl = rowData => {
  return rowData.live ? <StopIcon /> : <RecordMissionIcon disabled />
}

const SensorRecorderControl = rowData => {

  return rowData.live ? <StopIcon /> : <RecordSensorIcon color="error" />
}

const RecorderButtonFab = (rowData, userRoleAdmin, cb) => {

  const type = rowData.parentId === undefined ? 'platform' : 'sensor';

  let isDisabled = !userRoleAdmin;

  const tooltip = type === 'platform' ? `${!rowData.live ? 'Start' : 'Stop'} selected sensors` : `${!rowData.live ? 'Start' : 'Stop'}  sensor`;

  return (
    <Tooltip title={tooltip}>
      <Fab size="small" disabled={isDisabled} onClick={() => {

        if (cb) {
          const platformOrSensor = type === 'platform' ? {
            type: type,
            platformType: rowData.type,
            live: rowData.live,
            platformName: rowData.platform,
            sensors: rowData.tableData.childRows.map(s => { return { sensorName: s.name, checked: s.tableData.checked || false } }),
            checked: rowData.tableData.checked || false
          } :
            {
              type: type,
              live: rowData.live,
              platformName: rowData.parentPlatformName,
              platformType: rowData.platformType,
              platformLive: rowData.parentPlatformLive,
              sensorName: rowData.sensor,
              checked: rowData.tableData.checked || false
            };

          cb(platformOrSensor);
        }
      }} >
        {type === 'platform' ? PlatformRecorderControl(rowData) : SensorRecorderControl(rowData)}
      </Fab>
    </Tooltip>
  )
}

const PlatformState = props => {
  const rowData = props.data;

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handlePopoverOpen = (event) => {

    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const classes = useStyles();
  const open = Boolean(anchorEl) && rowData.sensor !== undefined && rowData.config && rowData.config.serverHostName !== null; // Open only for sensor

  return (
    <div
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}>
      <AvatarByState state={rowData.state} />
      <Popover
        id="mouse-over-popover"
        className={classes.popover}
        classes={{
          paper: classes.paper,
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <LiveSensorPopover data={rowData} />
      </Popover>
    </div>
  )
}


/**
 * Create table columns based on mode.
 * @param  {} admin
 * @param  {} cb
 */
const getColumns = (admin, userRoleAdmin, cb) => {

  const columns = [];

  if (admin) {
    columns.push({
      title: 'Record', field: 'state', tooltip: 'Record', width: '5%',
      render: rowData => { return RecorderButtonFab(rowData, userRoleAdmin, cb) }
    })
  }

  return [...columns,
  {
    title: 'State', field: 'state', tooltip: 'State', width: '5%',
    render: rowData => <PlatformState data={rowData} />
  },
  {
    title: 'Rec', field: 'recorder', tooltip: 'Recorder', width: '5%',
    render: rowData => rowData.platform || rowData.type === 'video' ? <RecorderState data={rowData} /> : null

  },
  {
    title: 'Name',
    field: 'name',
    tooltip: 'Platform name',
    render: rowData => (
      <span style={{ fontWeight: 600 }}>
        {rowData.name}
      </span>
    )
  },
  { title: 'Description', field: 'description', tooltip: 'Platform Description' },
  { title: 'Type', field: 'type', tooltip: 'Platform Type', width: '10%' },
  {
    title: 'Start Time', field: 'recordingStartTime', render: rowData => {
      if (rowData.type !== 'ais') {
        if (rowData.live)
          return (rowData.recordingStartTime !== undefined ? moment(rowData.recordingStartTime).format("MMM DD YYYY, HH:mm") : 'N/A')
        else
          return 'N/A';
      }
    }
  },
  {
    title: 'Duration', field: 'duration', render: rowData => {
      if (rowData.type !== 'ais') {
        return (rowData.duration !== undefined ? rowData.duration : 'N/A')
      }
    }
  },
  {
    title: 'Segments', field: 'segments', tooltip: 'Recorded segments', width: '5%', render: rowData => {
      const recorderInfo = rowData.recorderInfo ? rowData.recorderInfo.info : undefined;
      if (recorderInfo && rowData.live) {
        return recorderInfo.totalSegments ? recorderInfo.totalSegments : 'N/A';
      }
      else
        return '';
    }
  },
  ];
}


const PlatformTable = props => {

  const { server } = useContext(serverContext);
  const { platforms } = useContext(platformsContext);
  const [curTranslatedPlatforms, setCurTranslatedPlatforms] = useState([]);
  const { dispatchApp } = useContext(appContext);
  const [selectedRow, setSelectedRow] = useState(null);
  const tableTheme = useTableTheming(selectedRow);
  const [loading, setLoading] = useState(false);

  const actions = [];

  const updateLiveDuration = () => {

    if (platforms.availablePlatforms.length > 0) {
      const translatedPlatforms = translatePlatforms(platforms.availablePlatforms);

      const updatedPlatforms = translatedPlatforms.map(p => {

        if (p.live && p.recordingStartTime !== 'N/A') {
          const d = moment.duration(moment().diff(p.recordingStartTime));
          return { ...p, duration: d.humanize() }
        }
        else {
          return { ...p };
        }

      })
      setCurTranslatedPlatforms(updatedPlatforms);
    }
    else
      setCurTranslatedPlatforms([]);
  }


  useEffect(() => {

    updateLiveDuration();
    const interval = setInterval(() => {
      updateLiveDuration();
    }, 10000);

    return () => clearInterval(interval);
  }, [platforms]);


  useEffect(() => {
    if (Array.isArray(platforms.availablePlatforms) && platforms.availablePlatforms.length > 0) {
      const translatedPlatforms = translatePlatforms(platforms.availablePlatforms);
      setCurTranslatedPlatforms(translatedPlatforms);
    }

  }, [platforms.stateUpdateTime]);


  const handleRecordClick = async platformOrSensor => {

    try {
      setLoading(true);

      if (props.onCommand)
        props.onCommand('record', platformOrSensor);

      let message = `Platform ${platformOrSensor.platformName}`;
      if (platformOrSensor.type === 'sensor')
        message = message.concat(` Sensor ${platformOrSensor.sensorName}`);

      dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: message, severity: 'info' } });
      setLoading(false);
    } catch (error) {
      dispatchApp({ type: 'SHOW_ALERT', payload: { title: 'Error', message: `Error recording mission: ${error.response ? error.response.data : error.message}` } });
      setLoading(false);
    }
  }

  const handleSelectedRow = (evt, selectedRow) => {

    setSelectedRow(selectedRow)

    // For sensors
    if (props.onSelectedLiveSensors && selectedRow.parentId !== undefined) {
      const sensors = [selectedRow];
      props.onSelectedLiveSensors(sensors);
    }

    // For entire platform
    if (props.onSelectPlatform && selectedRow.parentId === undefined) {

      const sensors = selectedRow.tableData.childRows;
      props.onSelectedLiveSensors(sensors);

      const platform = { ...selectedRow, sensors: { ...sensors } };
      delete platform.tableData;

      props.onSelectPlatform(platform);
    }
  }


  const userRoleAdmin = server.userRole === 'superAdmin' || server.userRole === 'admin' || server.userRole === 'groupAdmin';
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <MaterialTable
        icons={tableIcons}
        columns={getColumns(props.admin, userRoleAdmin, handleRecordClick)}
        data={curTranslatedPlatforms}
        title={
          <Hidden only={['xs', 'sm']}>
            <MissionOrPlatformCounter title='Platforms' data={curTranslatedPlatforms} />
          </Hidden>
        }
        parentChildData={(row, rows) => rows.find(a => a.id === row.parentId)}
        onRowClick={handleSelectedRow}
        options={{
          cellStyle: {
            ...tableTheme.cellStyle,
            padding: '4px 8px',
            fontSize: '0.8rem',
          },
          headerStyle: {
            ...tableTheme.headerStyle,
            padding: '6px 8px',
            fontSize: '0.85rem',
            fontWeight: 600,
          },
          filterRowStyle: tableTheme.filterRowStyle,
          defaultExpanded: true,
          rowStyle: tableTheme.rowStyle,
          emptyRowsWhenPaging: false, // Fit height to actual rows instead of padding the page
        }}
        actions={actions}
        components={{
          Pagination: PatchedPagination,
        }}
      />
      {loading && <LinearProgress />}
    </div>
  );
}



export default PlatformTable;
